/**
 * Which physician, if any, a rep may name for a market.
 *
 * Lives beside `CLINIC_PHYSICIANS` rather than in the card so the rule is testable
 * without React, and so the one place that decides "may I say a name out loud"
 * sits next to the data it reads.
 */

import { CLINIC_PHYSICIANS } from './patient-access-script';

export interface PublishedPhysician {
  /** `null` means the market has no published physician — raise the do-not-name flag. */
  name: string | null;
}

/**
 * A market missing from `CLINIC_PHYSICIANS` resolves to `{ name: null }`, never to
 * `undefined`. That distinction is the whole point: `null` makes the card raise its
 * do-not-name flag, whereas `undefined` renders **no physician guidance at all**,
 * which invites a rep to improvise a name on a recorded call.
 *
 * It matters more since BAR-2597 pointed the Location select at the canonical
 * market registry. `CLINIC_PHYSICIANS` still holds placeholder names that predate
 * that registry and match almost none of it, so most real markets now miss the
 * lookup — and until the provider sheet lands, do-not-name is the correct answer
 * for them.
 *
 * `undefined` is reserved for "no market chosen yet", where there is nothing to say
 * either way.
 */
export function physicianFor(location: string | undefined): PublishedPhysician | undefined {
  if (!location) return undefined;

  return { name: CLINIC_PHYSICIANS[location] ?? null };
}
